import { useParams, useNavigate } from "react-router-dom";
import { blogs } from "../data/blogData";

export default function BlogDetails() {
  const { id } = useParams();
  const navigate = useNavigate();

  const blog = blogs.find((b) => b.id === id);

  if (!blog) {
    return <div className="text-center py-20">Blog not found</div>;
  }

  // ✅ Get latest related blogs
  const relatedBlogs = blogs
    .filter((b) => b.id !== id)
    .sort((a, b) => new Date(b.date) - new Date(a.date))
    .slice(0, 3);

  return (
    <div>
      {/* HERO SECTION */}
      <section
        className="relative h-[420px] md:h-[520px] flex justify-center items-start text-center overflow-hidden pt-20 mt-[-80px]"
        style={{
          backgroundImage: `
            linear-gradient(
              to bottom,
              rgba(255,255,255,1) 10%,
              rgba(255,255,255,0.7) 35%,
              rgba(255,255,255,0) 70%
            ),
            url(${blog.image})
          `,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
        }}
      >
        <div className="relative z-10 max-w-3xl px-6">
          <p className="text-sm text-gray-600 mb-3">
            {blog.category} • {blog.tag}
          </p>

          <h1 className="text-2xl md:text-4xl font-semibold text-gray-900">
            {blog.title}
          </h1>
        </div>
      </section>

      {/* AUTHOR SECTION */}
      <section className="max-w-6xl mx-auto px-6 mt-10">
        <div className="flex items-center justify-between border-b pb-6 flex-wrap gap-4">
          <div className="flex items-center gap-4">
            <img
              src={blog.authorImage}
              alt={blog.author}
              className="w-14 h-14 rounded-md object-cover"
            />

            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                {blog.author}
              </h3>
              <p className="text-sm text-gray-500">
                {blog.authorRole}
              </p>
            </div>
          </div>

          <div className="text-sm text-gray-500">
            Published on {blog.date}
          </div>
        </div>
      </section>

      {/* MAIN LAYOUT */}
      <section className="max-w-6xl mx-auto px-6 py-16 grid grid-cols-1 lg:grid-cols-3 gap-12">

        {/* LEFT - BLOG CONTENT */}
        <div className="lg:col-span-2">
          <div className="space-y-6">

            {blog.content.split("\n").map((line, index) => {
              const trimmed = line.trim();
              if (!trimmed) return null;

              // Bullet Points
              if (trimmed.startsWith("•")) {
                return (
                  <ul key={index} className="list-disc pl-6 text-gray-700 text-lg">
                    <li>{trimmed.replace("•", "").trim()}</li>
                  </ul>
                );
              }

              // Headings
              if (
                (trimmed.length < 80 && trimmed === trimmed.toUpperCase()) ||
                trimmed.endsWith(":")
              ) {
                return (
                  <h2
                    key={index}
                    className="text-2xl md:text-xl font-semibold text-gray-900 mt-8"
                  >
                    {trimmed}
                  </h2>
                );
              }

              // Paragraph
              return (
                <p
                  key={index}
                  className="text-gray-700 text-lg leading-relaxed"
                >
                  {trimmed}
                </p>
              );
            })}

          </div>
        </div>

        {/* RIGHT - SIDEBAR */}
        <div className="lg:col-span-1">
          <div className="sticky top-24 space-y-8">

            {/* SUBSCRIBE BOX */}
            <div className="border rounded-xl p-6 shadow-sm">
              <h3 className="text-lg font-semibold mb-2">
                Subscribe for Insights
              </h3>

              <p className="text-sm text-gray-500 mb-4">
                Get the latest updates and expert advice.
              </p>

              <input
                type="email"
                placeholder="Your Email"
                className="w-full border rounded-md px-3 py-2 mb-3 focus:outline-none focus:ring-2 focus:ring-green-500"
              />

              <button className="w-full bg-green-600 text-white py-2 rounded-md hover:bg-green-700 transition">
                Subscribe
              </button>
            </div>

            {/* RELATED ARTICLES */}
            <div>
              <h3 className="text-lg font-semibold mb-4">
                Related Articles
              </h3>

              <div className="space-y-4">
                {relatedBlogs.map((item) => (
                  <div
                    key={item.id}
                    onClick={() => navigate(`/blog/${item.id}`)}
                    className="flex gap-3 items-center cursor-pointer group"
                  >
                    <img
                      src={item.image}
                      alt={item.title}
                      className="w-16 h-16 object-cover rounded-md"
                    />

                    <p className="text-sm text-gray-800 font-medium group-hover:text-green-600 transition">
                      {item.title}
                    </p>
                  </div>
                ))}
              </div>
            </div>

          </div>
        </div>

      </section>
    </div>
  );
}